import * as React from 'react';
import { Tabs, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { cn } from '@/shared/lib/utils';

/**
 * ResponsiveTabs - Adaptive tabs that automatically handle desktop and mobile layouts
 *
 * Features:
 * - Desktop: Tabs spread across full width (up to 5 tabs)
 * - Mobile: Horizontal scroll for many tabs
 * - Automatic layout switching based on screen size and tab count
 * - Scroll indicators for hidden tabs on mobile
 * - Full accessibility support
 * - Touch-optimized interactions
 *
 * @example
 * // Simple usage - automatically responsive
 * <ResponsiveTabs defaultValue="tab1">
 *   <ResponsiveTabsList>
 *     <ResponsiveTabsTrigger value="tab1">Tab 1</ResponsiveTabsTrigger>
 *     <ResponsiveTabsTrigger value="tab2">Tab 2</ResponsiveTabsTrigger>
 *     <ResponsiveTabsTrigger value="tab3">Tab 3</ResponsiveTabsTrigger>
 *   </ResponsiveTabsList>
 *   <TabsContent value="tab1">Content 1</TabsContent>
 *   <TabsContent value="tab2">Content 2</TabsContent>
 *   <TabsContent value="tab3">Content 3</TabsContent>
 * </ResponsiveTabs>
 *
 * @example
 * // Disable scroll indicators
 * <ResponsiveTabsList showScrollIndicators={false}>
 *   ...
 * </ResponsiveTabsList>
 */

export interface ResponsiveTabsProps extends React.ComponentPropsWithoutRef<
  typeof Tabs
> {
  readonly className?: string;
}

const ResponsiveTabs = React.forwardRef<
  React.ComponentRef<typeof Tabs>,
  ResponsiveTabsProps
>(({ className, ...props }, ref) => (
  <Tabs ref={ref} className={cn('w-full', className)} {...props} />
));
ResponsiveTabs.displayName = 'ResponsiveTabs';

export interface ResponsiveTabsListProps extends React.ComponentPropsWithoutRef<
  typeof TabsList
> {
  readonly showScrollIndicators?: boolean;
  readonly tabCount?: number;
}

const ResponsiveTabsList = React.forwardRef<
  React.ComponentRef<typeof TabsList>,
  ResponsiveTabsListProps
>(
  (
    { className, showScrollIndicators = true, tabCount, children, ...props },
    ref
  ) => {
    const [showLeftIndicator, setShowLeftIndicator] = React.useState(false);
    const [showRightIndicator, setShowRightIndicator] = React.useState(false);
    const scrollRef = React.useRef<HTMLDivElement>(null);

    // Auto-detect tab count from children
    const childCount = React.useMemo(() => {
      if (tabCount !== undefined) return tabCount;
      return React.Children.count(children);
    }, [tabCount, children]);

    const checkScroll = React.useCallback(() => {
      const element = scrollRef.current;
      if (!element || !showScrollIndicators) return;

      const { scrollLeft, scrollWidth, clientWidth } = element;
      setShowLeftIndicator(scrollLeft > 0);
      setShowRightIndicator(scrollLeft + clientWidth < scrollWidth - 1);
    }, [showScrollIndicators]);

    React.useEffect(() => {
      checkScroll();
      window.addEventListener('resize', checkScroll);
      return () => window.removeEventListener('resize', checkScroll);
    }, [checkScroll]);

    const handleScroll = () => {
      checkScroll();
    };

    // Determine layout classes based on tab count and screen size
    const getLayoutClasses = () => {
      // On mobile (< md), always use scrollable layout
      // On desktop (>= md), use grid for 2-5 tabs, scrollable for 6+
      if (childCount <= 2) {
        return 'w-full md:grid md:grid-cols-2';
      }
      if (childCount === 3) {
        return 'w-full md:grid md:grid-cols-3';
      }
      if (childCount === 4) {
        return 'w-full md:grid md:grid-cols-4';
      }
      if (childCount === 5) {
        return 'w-full md:grid md:grid-cols-5';
      }
      // 6+ tabs: always scrollable
      return 'w-full justify-start';
    };

    return (
      <div className="relative w-full">
        {/* Left scroll indicator */}
        {showScrollIndicators && showLeftIndicator && (
          <div className="pointer-events-none absolute left-0 top-0 z-10 h-full w-8 bg-gradient-to-r from-background to-transparent md:hidden" />
        )}

        {/* Tabs list with adaptive layout */}
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="scrollbar-thin overflow-x-auto md:overflow-x-visible"
        >
          <TabsList
            ref={ref}
            className={cn(getLayoutClasses(), className)}
            {...props}
          >
            {children}
          </TabsList>
        </div>

        {/* Right scroll indicator */}
        {showScrollIndicators && showRightIndicator && (
          <div className="pointer-events-none absolute right-0 top-0 z-10 h-full w-8 bg-gradient-to-l from-background to-transparent md:hidden" />
        )}
      </div>
    );
  }
);
ResponsiveTabsList.displayName = 'ResponsiveTabsList';

const ResponsiveTabsTrigger = React.forwardRef<
  React.ComponentRef<typeof TabsTrigger>,
  React.ComponentPropsWithoutRef<typeof TabsTrigger>
>(({ className, children, ...props }, ref) => (
  <TabsTrigger ref={ref} className={cn('shrink-0', className)} {...props}>
    {children}
  </TabsTrigger>
));
ResponsiveTabsTrigger.displayName = 'ResponsiveTabsTrigger';

export { ResponsiveTabs, ResponsiveTabsList, ResponsiveTabsTrigger };
export { TabsContent } from '@/shared/components/ui/tabs';
